import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheckDouble, FaPrint } from "react-icons/fa";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import { useU2000ExportExcelHandlers } from "../../../u2000Core/hooks/handler/U2000UseExportExcelHandlers";
import { useU2000ExportExcelNotification } from "../../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { useNavigate } from "react-router-dom";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../../u2000Core/components/tables/basic/U2000BasicTable";
import type { U2000TableColumn } from "../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { u2000DefaultSortOrderForColumn } from "../../../u2000Core/tools/U2000DefaultSortOrderForColumn";
import { U2000GridStateEnumPaginationDirection } from "../../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../../u2000Core/reducer/formReducer/U2000LUState";
import type { U2000SearchParameter } from "../../../u2000Core/reducer/formReducer/U2000SearchState";
import type { U2000TableHandlers } from "../../../u2000Core/types/U2000TableHandlers";
import { useU2000LogicalUnitContext } from "../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { useU3130Context } from "../../U3130Context";
import type { U3130Timesheet } from "../../U3130TimesheetModel";
import type { U3130ApprovalListPreset } from "../../tools/u3130ApprovalListPreset";
import { u3130ApplyApprovalListPresetToSearchState, u3130MapApprovalListPresetToApi } from "../../tools/u3130ApprovalListPreset";
import { u3130MapActivePayrollPeriodsToReportOptions } from "../../tools/u3130ActivePayrollPeriodReport";
import { NS, U3130ROLES } from "../../U3130";
import { extractBaseLanguage } from "../../../u2000Core/tools/U2000LanguageUtils";
import {
  doExportDepensesParSemaineParCodePdf,
  doExportEmployeurD,
  doExportSommairePaiePdf,
  doApproveBatch,
  doExportExcel,
  doSearch,
  luHandleApiError,
  type U3130ApproveBatchResult,
  type U3130SearchPayload,
} from "../../U3130Service";
import { useU3130ApproveBatchHandlers } from "../../U3130UseApproveBatchHandlers";
import U3130ApproveBatchDialog from "./U3130ApproveBatchDialog";
import { useU3130ExportDepensesParSemaineParCodePdfHandlers } from "../../U3130UseExportDepensesParSemaineParCodePdfHandlers";
import { useU3130ExportEmployeurDHandlers } from "../../U3130UseExportEmployeurDHandlers";
import { useU3130ExportSommairePaiePdfHandlers } from "../../U3130UseExportSommairePaiePdfHandlers";
import U3130SommairePaieReportDialog from "./U3130SommairePaieReportDialog";
import { u3130GetTimesheetListColumns } from "../../tools/U3130TimesheetListColumns";
import {
  U2000_CRITERIA_LOOKUP_U3130_FIND_EMPLOYEE,
  defaultItemToDisplay,
  defaultItemToSelectedDisplay,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from "../../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import { useU3130FindEmployeeHandlers } from "../../U3130UseFindProjectSubprojectHandlers";

const U3130TimesheetList: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const {
    initLU,
    luStateHandlers,
    searchStateHandlers,
    gridStateHandlers,
    approvalListPreset,
    setApprovalListPreset,
    approvalListPresetInitialized,
    setApprovalListPresetInitialized,
  } = useU3130Context();
  const didRunInitialListSearchRef = useRef(false);
  const approvalListPresetRef = useRef(approvalListPreset);
  approvalListPresetRef.current = approvalListPreset;
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const columnCount = searchStateHandlers.getColumns().length;
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [approveBatchDialogOpen, setApproveBatchDialogOpen] = useState(false);
  const [approveBatchPreview, setApproveBatchPreview] = useState<U3130ApproveBatchResult | null>(null);
  const approveBatchFilterRef = useRef<U2000SearchParameter | null>(null);
  const reportLang = useMemo(() => extractBaseLanguage(i18n.language), [i18n.language]);
  const canReadList = hasRole(U3130ROLES.READ);
  const canApproveBatch = approvalListPreset === "waiting_for_approval" && hasRole(U3130ROLES.EDIT);

  const batchApiOnError = useCallback(
    ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const handleApproveBatch = useU3130ApproveBatchHandlers({
    doApproveBatchFn: async (body) => await doApproveBatch(u2000AxiosAuthenticated, body),
    onError: batchApiOnError,
  });
  const findEmployeeHandlers = useU3130FindEmployeeHandlers();

  useEffect(() => {
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3130_FIND_EMPLOYEE, {
      search: findEmployeeHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
      itemToSelectedDisplay: defaultItemToSelectedDisplay,
    });
    return () => unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3130_FIND_EMPLOYEE);
  }, [findEmployeeHandlers.search]);

  const reportPdfOnError = useCallback(
    ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const handleSommairePaiePdf = useU3130ExportSommairePaiePdfHandlers({
    doExportSommairePaiePdfFn: async (body) => await doExportSommairePaiePdf(u2000AxiosAuthenticated, body),
    onSuccess: () => {
      toast.success(t("msgReportGenerationStarted", { ns: NS }), t("successTitle", { ns: "common" }));
      setReportDialogOpen(false);
    },
    onError: reportPdfOnError,
  });

  const handleDepensesParSemaineParCodePdf = useU3130ExportDepensesParSemaineParCodePdfHandlers({
    doExportDepensesParSemaineParCodePdfFn: async (body) => await doExportDepensesParSemaineParCodePdf(u2000AxiosAuthenticated, body),
    onSuccess: () => {
      toast.success(t("msgReportGenerationStarted", { ns: NS }), t("successTitle", { ns: "common" }));
      setReportDialogOpen(false);
    },
    onError: reportPdfOnError,
  });

  const handleEmployeurD = useU3130ExportEmployeurDHandlers({
    doExportEmployeurDFn: async (body) => await doExportEmployeurD(u2000AxiosAuthenticated, body),
    onSuccess: () => {
      toast.success(t("msgReportGenerationStarted", { ns: NS }), t("successTitle", { ns: "common" }));
      setReportDialogOpen(false);
    },
    onError: reportPdfOnError,
  });

  const isAnyReportPdfExporting =
    handleSommairePaiePdf.isExporting || handleDepensesParSemaineParCodePdf.isExporting || handleEmployeurD.isExporting;

  const payrollPeriodOptionsForReport = useMemo(() => {
    const active = initLU.activePayrollPeriod;
    if (active?.length) {
      return u3130MapActivePayrollPeriodsToReportOptions(active, t, NS);
    }
    return initLU.sommairePaiePayrollPeriods ?? [];
  }, [initLU.activePayrollPeriod, initLU.sommairePaiePayrollPeriods, t]);

  const reportToolbarSlot = useMemo(() => {
    if (!canReadList) return null;
    const reportLabel = t("btnReportToolbar", { ns: NS });
    return (
      <button
        type="button"
        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-transparent bg-transparent h-9 min-h-9 px-2 text-gray-600 transition-colors duration-200
          hover:border-gray-200 hover:bg-white hover:text-blue-600 hover:shadow-xs
          focus:outline-hidden focus:ring-2 focus:ring-blue-500
          disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => setReportDialogOpen(true)}
        disabled={isAnyReportPdfExporting}
        aria-label={reportLabel}
        title={reportLabel}
      >
        <FaPrint size={14} className="shrink-0" aria-hidden />
        <span className="hidden max-w-22 truncate text-xs font-medium sm:inline">{reportLabel}</span>
      </button>
    );
  }, [canReadList, t, isAnyReportPdfExporting]);

  useEffect(() => {
    const columns = u3130GetTimesheetListColumns(t);
    searchStateHandlers.setColumns(columns);
    gridStateHandlers.setColumns(columns);
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
    const currentFields = searchStateHandlers.getFields();
    if (currentFields.length === 0) {
      searchStateHandlers.setFields(columns.filter((col: U2000TableColumn) => col.isDefaultColumn).map((col: U2000TableColumn) => ({ field: { ...col } })));
    }
    const currentSortedFields = searchStateHandlers.getSortedFields();
    if (currentSortedFields.length === 0) {
      searchStateHandlers.setSortedFields(
        columns
          .filter((col: U2000TableColumn) => col.isDefaultSortedColumn)
          .map((col: U2000TableColumn) => ({ field: { ...col }, sortOrder: u2000DefaultSortOrderForColumn(col) })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init colonnes / breadcrumb
  }, [t]);

  useU2000ExportExcelNotification();

  const handleExportExcel = useU2000ExportExcelHandlers({
    doExportExcelFn: async (filter, lang) => {
      return await doExportExcel(u2000AxiosAuthenticated, filter, lang, u3130MapApprovalListPresetToApi(approvalListPresetRef.current));
    },
    i18nLanguage: i18n.language,
    onSuccess: (processId: string) => {
      toast.success(
        t("msgExportExcelStarted", { processId, ns: "common" }) || `Export en cours (référence: ${processId})`,
        t("successTitle", { ns: "common" }),
      );
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSearch = useU2000SearchHandlers<U3130Timesheet>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      const extended: U3130SearchPayload = {
        ...searchPayload,
        approvalListPreset: u3130MapApprovalListPresetToApi(approvalListPresetRef.current),
      };
      return await doSearch(u2000AxiosAuthenticated, extended);
    },
    onSuccess: (searchResult: U2000SearchResult<U3130Timesheet>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  /**
   * Lance la recherche avec un `searchParameter` explicite (ne dépend pas du state reducer
   * qui peut être encore périmé à cause des closures React).
   */
  const fireSearch = useCallback(
    (searchParam: U2000SearchParameter) => {
      handleSearch.handleSearch(searchParam, gridStateHandlers.extractPagination(1), U2000GridStateEnumPaginationDirection.NEXTPAGE);
    },
    [gridStateHandlers, handleSearch],
  );

  const fireSearchRef = useRef(fireSearch);
  fireSearchRef.current = fireSearch;

  useEffect(() => {
    if (columnCount === 0) return;
    if (approvalListPresetInitialized) return;
    const saved = searchStateHandlers.getSavedFilters();
    if (saved?.some((f) => f.isDefaultFilter)) {
      setApprovalListPresetInitialized(true);
      return;
    }
    const cr = searchStateHandlers.getCriterias();
    if (cr.length > 0 && cr.some((c) => c.field.name === "status")) {
      setApprovalListPresetInitialized(true);
      return;
    }
    const preset = initLU.defaultApprovalListPreset ?? "waiting_for_approval";
    approvalListPresetRef.current = preset;
    setApprovalListPreset(preset);
    const searchParam = u3130ApplyApprovalListPresetToSearchState(preset, searchStateHandlers, t);
    setApprovalListPresetInitialized(true);

    if (searchParam && !didRunInitialListSearchRef.current) {
      didRunInitialListSearchRef.current = true;
      fireSearchRef.current(searchParam);
    }
  }, [
    approvalListPresetInitialized,
    columnCount,
    initLU.defaultApprovalListPreset,
    searchStateHandlers,
    setApprovalListPreset,
    setApprovalListPresetInitialized,
    t,
  ]);

  const closeApproveBatchDialog = useCallback(() => {
    setApproveBatchDialogOpen(false);
    setApproveBatchPreview(null);
    approveBatchFilterRef.current = null;
  }, []);

  const onApproveBatchToolbarClick = useCallback(async () => {
    const filter = searchStateHandlers.extractSearchParameter();
    approveBatchFilterRef.current = filter;
    setApproveBatchPreview(null);
    setApproveBatchDialogOpen(true);
    const preview = await handleApproveBatch.previewBatch(filter);
    if (preview) {
      setApproveBatchPreview(preview);
    } else {
      closeApproveBatchDialog();
    }
  }, [closeApproveBatchDialog, handleApproveBatch, searchStateHandlers]);

  const onApproveBatchConfirm = useCallback(async () => {
    const filter = approveBatchFilterRef.current;
    if (!filter) return;
    const result = await handleApproveBatch.executeBatch(filter);
    if (!result) return;
    const summaryKey = result.errorCount > 0 ? "msgApproveBatchSummaryWithErrors" : "msgApproveBatchSummary";
    toast.success(
      t(summaryKey, {
        ns: NS,
        approvedCount: result.approvedCount,
        skippedCount: result.skippedCount,
        errorCount: result.errorCount,
      }),
      t("successTitle", { ns: "common" }),
    );
    closeApproveBatchDialog();
    fireSearchRef.current(filter);
  }, [closeApproveBatchDialog, handleApproveBatch, t, toast]);

  const approveBatchToolbarSlot = useMemo(() => {
    if (!canApproveBatch) return null;
    return (
      <U2000ActionBtn
        label={
          handleApproveBatch.isRunning && !approveBatchDialogOpen
            ? t("btnApproveBatchPending", { ns: NS })
            : t("btnApproveBatch", { ns: NS })
        }
        handleClick={() => void onApproveBatchToolbarClick()}
        disabled={handleApproveBatch.isRunning}
        icon={FaCheckDouble}
        hasRole
      />
    );
  }, [approveBatchDialogOpen, canApproveBatch, handleApproveBatch.isRunning, onApproveBatchToolbarClick, t]);

  const onApprovalPresetChange = useCallback(
    (next: U3130ApprovalListPreset) => {
      approvalListPresetRef.current = next;
      setApprovalListPreset(next);
      const searchParam = u3130ApplyApprovalListPresetToSearchState(next, searchStateHandlers, t);
      if (searchParam) {
        fireSearchRef.current(searchParam);
      }
    },
    [searchStateHandlers, setApprovalListPreset, t],
  );

  const approvalPresetCollapsedSlot = useMemo(
    () => (
      <>
        {(
          [
            { id: "waiting_for_approval" as const, labelKey: "presetApprovalWaiting" },
            { id: "processed_current_week" as const, labelKey: "presetApprovalProcessedCurrentWeek" },
            { id: "processed_previous_week" as const, labelKey: "presetApprovalProcessedPreviousWeek" },
            { id: "processed_all_weeks" as const, labelKey: "presetApprovalProcessedAllWeeks" },
          ] as const
        ).map(({ id, labelKey }, idx, arr) => {
          const selected = approvalListPreset === id;
          return (
            <React.Fragment key={id}>
              <button
                type="button"
                onClick={() => onApprovalPresetChange(id)}
                aria-pressed={selected}
                className={`shrink-0 rounded-lg border border-transparent px-3 py-1.5 text-sm transition-all duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 ${
                  selected ? "border-blue-200 bg-blue-50 font-semibold text-blue-900 shadow-xs" : "font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                {t(labelKey, { ns: NS })}
              </button>
              {idx < arr.length - 1 && <div className="mx-2 h-4 w-px shrink-0 bg-gray-300" aria-hidden />}
            </React.Fragment>
          );
        })}
      </>
    ),
    [approvalListPreset, onApprovalPresetChange, t],
  );

  const handlerActions: U2000TableHandlers<U3130Timesheet> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3130Timesheet) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      if (currentIdx >= 0) {
        gridStateHandlers.setCurrentIdx(currentIdx);
      }
      navigate(`/U3130/${row.id}`);
    },
    onExportExcel: async () => {
      const filter = searchStateHandlers.extractSearchParameter();
      await handleExportExcel.handleExportExcel(filter);
    },
  };

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto">
      <U2000BasicTable<U3130Timesheet>
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        isRowSelectable={() => false}
        label={t("lblTimesheetList", { count: gridStateHandlers.getTotalRows(), ns: NS })}
        actionsButtons={approveBatchToolbarSlot}
        handlerActions={handlerActions}
        allowRowSelection={false}
        hideSearchPanel={initLU.hideSearch}
        searchCollapsedLeadingSlot={approvalPresetCollapsedSlot}
        showCollapsedCriteriaChips={false}
        exportExcelTitle={t("btnExportExcel", { ns: "common" })}
        isExporting={handleExportExcel.isExporting}
        hasExportRole={hasRole(U3130ROLES.READ)}
        extraSearchActions={reportToolbarSlot}
      />
      <U3130ApproveBatchDialog
        open={approveBatchDialogOpen}
        preview={approveBatchPreview}
        isExecuting={handleApproveBatch.isRunning}
        onConfirm={() => void onApproveBatchConfirm()}
        onClose={closeApproveBatchDialog}
      />
      <U3130SommairePaieReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        payrollPeriodOptions={payrollPeriodOptionsForReport}
        reportLang={reportLang}
        isExportingSommairePaie={handleSommairePaiePdf.isExporting}
        isExportingDepensesParSemaineParCode={handleDepensesParSemaineParCodePdf.isExporting}
        isExportingEmployeurD={handleEmployeurD.isExporting}
        onSubmitSommairePaie={(body) => void handleSommairePaiePdf.exportSommairePaiePdf(body)}
        onSubmitDepensesParSemaineParCode={(body) => void handleDepensesParSemaineParCodePdf.exportDepensesParSemaineParCodePdf(body)}
        onSubmitEmployeurD={(body) => void handleEmployeurD.exportEmployeurD(body)}
      />
    </div>
  );
};

export default U3130TimesheetList;
